/**
 * Iter 231 · Preview of tomorrow's Monday private-reminder digest.
 *
 * Shows the user which private reminders will land in their inbox and lets
 * them dismiss any they don't want to be nagged about — dismiss is
 * per-week, so a genuine reminder still fires next Monday if not
 * completed. Also allows undismiss.
 *
 * Rendered as a modal overlay so it can be opened either from the
 * RemindersPanel header or via a deep link like
 * `/app/team-reminders?preview=1`.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Lock, X, Loader2, Check, RotateCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })
             + " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function DigestPreviewModal({ open, onClose }) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId || !open) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/reminders/digest-preview`);
      setData(data);
    } catch { setData({ included: [], dismissed: [], counts: { included: 0, dismissed: 0, total: 0 }, digest_monday: "" }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, open]);

  if (!open) return null;

  const dismiss = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/reminders/${r.id}/dismiss-from-digest`);
      toast.success("Removed from tomorrow's email");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to dismiss"));
    } finally { setBusyId(null); }
  };

  const undismiss = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/reminders/${r.id}/undismiss-from-digest`);
      toast.success("Restored to tomorrow's email");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to restore"));
    } finally { setBusyId(null); }
  };

  const included = data?.included || [];
  const dismissed = data?.dismissed || [];

  // R138f · Feb 2026 — Portal to document.body so the fixed overlay
  // escapes the Rnd/Popover transform ancestor that was previously
  // clipping the modal into the panel's coord space and making it
  // impossible to close.
  return createPortal((
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4"
           onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
           data-testid="digest-preview-modal">
      <div className="bg-background border border-border w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            <div>
              <div className="text-sm font-bold">Monday digest preview</div>
              <div className="text-[11px] text-muted-foreground">
                {data?.digest_monday ? `Landing in your inbox on ${data.digest_monday}` : "Loading…"}
                {data?.counts ? ` · ${data.counts.included} queued · ${data.counts.dismissed} dismissed` : ""}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"
                    data-testid="digest-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
          ) : (
            <>
              <section className="p-4 space-y-2" data-testid="digest-preview-included">
                <div className="label-eyebrow">Will be in the email ({included.length})</div>
                {included.length === 0 ? (
                  <p className="text-xs text-muted-foreground border border-dashed border-border py-6 text-center">
                    No private reminders queued for tomorrow — enjoy the quiet.
                  </p>
                ) : included.map((r) => (
                  <div key={r.id} className="border border-border p-2.5 flex items-start gap-2"
                          data-testid={`digest-preview-item-${r.id}`}>
                    <Lock className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{r.title}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {fmtWhen(r.remind_at || r.due_at)}
                      </div>
                      {r.note && <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{r.note}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={() => dismiss(r)}
                      disabled={busyId === r.id}
                      className="text-[10px] px-2 py-1 border border-border hover:border-rose-400 hover:text-rose-600 transition inline-flex items-center gap-1 disabled:opacity-50 shrink-0"
                      data-testid={`digest-preview-dismiss-${r.id}`}
                    >
                      {busyId === r.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <X className="w-2.5 h-2.5" />}
                      Dismiss
                    </button>
                  </div>
                ))}
              </section>
              {dismissed.length > 0 && (
                <section className="p-4 border-t border-border space-y-2 bg-muted/20"
                            data-testid="digest-preview-dismissed">
                  <div className="label-eyebrow">Dismissed for this week ({dismissed.length})</div>
                  {dismissed.map((r) => (
                    <div key={r.id} className="border border-border p-2.5 flex items-start gap-2 opacity-75"
                            data-testid={`digest-preview-dismissed-item-${r.id}`}>
                      <Lock className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-through">{r.title}</div>
                        <div className="text-[11px] text-muted-foreground">{fmtWhen(r.remind_at || r.due_at)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => undismiss(r)}
                        disabled={busyId === r.id}
                        className="text-[10px] px-2 py-1 border border-border hover:border-primary hover:text-primary transition inline-flex items-center gap-1 disabled:opacity-50 shrink-0"
                        data-testid={`digest-preview-restore-${r.id}`}
                      >
                        {busyId === r.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5" />}
                        Restore
                      </button>
                    </div>
                  ))}
                </section>
              )}
            </>
          )}
        </div>
        <div className="px-4 py-2.5 border-t border-border bg-muted/30 text-[10px] text-muted-foreground">
          <Check className="w-2.5 h-2.5 inline -mt-0.5 mr-1" />
          Dismissing skips just this week — the reminder still fires next Monday if you haven&apos;t completed it.
        </div>
      </div>
    </div>
  ), document.body);
}
